import { useEffect, useState } from 'react';
import { t } from '../../lib/i18n';
import { pollPairing, startPairing } from './session';
import type { DisplaySession } from './session';

/** "123456789" -> "123 456 789", easy to read from across a room. */
export const spacedCode = (code: string): string => `${code.slice(0, 3)} ${code.slice(3, 6)} ${code.slice(6)}`;

const RETRY_MS = 10_000;

type Note = 'waiting' | 'expired' | 'retrying';

/**
 * Shows a 9-digit code, polls until someone enters it in the portal, and hands the device token (sent once) to the app.
 * A code that runs out of time is replaced by a new one.
 */
export function PairingScreen({ onPaired }: { onPaired: (s: DisplaySession) => void }) {
  const [code, setCode] = useState<string | null>(null);
  const [note, setNote] = useState<Note>('waiting');

  useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const later = (fn: () => void, ms: number) => {
      if (live) timer = setTimeout(fn, ms);
    };

    const begin = async (): Promise<void> => {
      let started;
      try {
        started = await startPairing();
      } catch {
        if (!live) return;
        setNote('retrying');
        later(() => void begin(), RETRY_MS);
        return;
      }
      if (!live) return;
      setCode(started.code);
      setNote('waiting');
      const every = Math.max(1, started.pollEverySeconds) * 1000;
      const expiresAt = new Date(started.expiresAt).getTime();
      const poll = async (): Promise<void> => {
        if (!live) return;
        if (Date.now() >= expiresAt) {
          setNote('expired');
          later(() => void begin(), 2000);
          return;
        }
        try {
          const r = await pollPairing(started.pollSecret);
          if (!live) return;
          if (r.status === 'paired') {
            onPaired({ token: r.token, deviceId: r.deviceId, siteId: r.siteId });
            return;
          }
          if (r.status === 'expired') {
            setNote('expired');
            later(() => void begin(), 2000);
            return;
          }
          setNote('waiting');
        } catch {
          if (!live) return;
          setNote('retrying');
        }
        later(() => void poll(), every);
      };
      later(() => void poll(), every);
    };
    void begin();
    return () => {
      live = false;
      if (timer) clearTimeout(timer);
    };
    // onPaired is stable for the life of the screen; pairing must not restart when the parent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="fixed inset-0 flex cursor-none flex-col items-center justify-center gap-[4vmin] bg-slate-900 p-[4vmin] text-center text-white">
      <h1 className="text-[clamp(2rem,5vmin,5rem)] font-semibold">{t('displayUi.pair.title')}</h1>
      <p className="max-w-[90vw] text-[clamp(1.5rem,3.2vmin,3.5rem)] text-slate-200">{t('displayUi.pair.instruction')}</p>
      {code ? (
        <p
          className="rounded-[2vmin] bg-white px-[5vmin] py-[3vmin] text-[clamp(3rem,13vmin,12rem)] font-bold leading-none tracking-[0.12em] text-slate-900"
          aria-label={t('displayUi.pair.codeLabel', { code: code.split('').join(' ') })}
        >
          <span aria-hidden="true">{spacedCode(code)}</span>
        </p>
      ) : null}
      <p role="status" className="text-[clamp(1.25rem,2.6vmin,2.5rem)] text-slate-300">
        {note === 'waiting' ? t('displayUi.pair.waiting') : note === 'expired' ? t('displayUi.pair.expired') : t('displayUi.pair.retrying')}
      </p>
    </main>
  );
}
